/**
 * Client for the gcui-art/suno-api proxy (deployed separately by the
 * vibez.surf operator). BYOK model — each user supplies their OWN Suno
 * session cookie, which is stored in localStorage and sent to the proxy
 * as the `X-Suno-Cookie` header. The proxy URL itself is baked in at
 * build time via `VITE_SUNO_API_URL`.
 *
 * Endpoints used:
 *   POST /api/generate        — kicks off a generation
 *   GET  /api/get?ids=<id>    — polls status / fetches audio_url
 *
 * Security: the cookie never leaves the user's browser except on requests
 * to the configured proxy. We never log it, never embed it in URLs, and
 * rely on localStorage's same-origin isolation for at-rest protection.
 * Users see a password-masked input in the settings modal.
 */

export interface SunoSongMeta {
  id: string;
  status: string;
  audio_url?: string;
  title?: string;
}

const TOKEN_KEY = 'vibez.surf.sunoToken';
const MODEL_KEY = 'vibez.surf.sunoModel';

/**
 * Suno model IDs. Suno's newer models use internal codenames (e.g. v5.5
 * ships as `chirp-crow`) rather than version-numbered ids. IDs here were
 * verified against Suno's own frontend payload on suno.com — if a future
 * version ships, grab the new id from Suno's DevTools Network tab on
 * `/api/generate/v2-web/` (`mv` field) and add it here.
 *
 * Order is newest → oldest; first entry is the default when nothing's saved.
 */
export const SUNO_MODELS = [
  { id: 'chirp-crow', label: 'v5' },
  { id: 'chirp-v4-5', label: 'v4.5' },
  { id: 'chirp-v4',   label: 'v4' },
  { id: 'chirp-v3-5', label: 'v3.5' },
] as const;
export type SunoModelId = typeof SUNO_MODELS[number]['id'];
export const DEFAULT_SUNO_MODEL: SunoModelId = 'chirp-crow';

export function loadSunoModel(): SunoModelId {
  const raw = (() => { try { return localStorage.getItem(MODEL_KEY); } catch { return null; } })();
  const hit = SUNO_MODELS.find((m) => m.id === raw);
  return hit ? hit.id : DEFAULT_SUNO_MODEL;
}
export function saveSunoModel(m: SunoModelId): void {
  localStorage.setItem(MODEL_KEY, m);
}

/** Abort primary request after this many ms. Vercel free kills functions
 *  at 10s but returns a 5xx rather than hanging, so this mostly catches
 *  the cases where Vercel itself (not the function) is slow. 20s gives a
 *  comfortable window without making users wait forever on a dead primary. */
const PRIMARY_TIMEOUT_MS = 20_000;

/** Fallback cold-start retry policy. Render free-tier proxies close the
 *  first request with ERR_CONNECTION_CLOSED / 502 / 503 while the container
 *  boots (~30–60s). We retry the last URL a few times with spacing so the
 *  user rides through the cold start instead of seeing a hard failure. */
const FALLBACK_MAX_ATTEMPTS = 4;
const FALLBACK_RETRY_DELAY_MS = 10_000;
const RETRYABLE_STATUSES = new Set([500, 502, 503, 504]);

function getApiUrls(): string[] {
  const primary = (import.meta.env.VITE_SUNO_API_URL as string | undefined) || '';
  const fallback = (import.meta.env.VITE_SUNO_API_URL_FALLBACK as string | undefined) || '';
  return [primary, fallback].filter(Boolean);
}

/** True when the deployer configured at least one proxy URL. Gates the UI. */
export function isSunoEnabled(): boolean {
  return getApiUrls().length > 0;
}

/**
 * Saved tokens self-expire after this many days. Suno cookies last a few
 * weeks in practice, so we pick a window that's typically shorter than the
 * real lifetime — any value that expires on our side before Suno revokes
 * shrinks the window where a leaked cookie (stolen dev-console session,
 * shared device) remains useful.
 */
const TOKEN_TTL_DAYS = 21;
const TOKEN_TTL_MS = TOKEN_TTL_DAYS * 24 * 60 * 60 * 1000;

interface StoredToken {
  token: string;
  /** Unix ms when the token was saved. Null means pre-TTL schema (bare string). */
  savedAt: number;
}

/**
 * Read the stored token, applying TTL expiry. Returns null if:
 *   - nothing's stored
 *   - the stored entry is older than TOKEN_TTL_DAYS (entry is auto-cleared)
 *   - localStorage throws (Safari private mode, etc.)
 *
 * Backward-compatible with the original schema where the value was a bare
 * string with no expiry — those entries are auto-migrated to the new
 * schema on read, stamped with `savedAt = now`, so they get a fresh TTL.
 */
export function loadSunoToken(): string | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  // New schema: JSON { token, savedAt }. Legacy: plain cookie string.
  let entry: StoredToken;
  if (raw.startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as Partial<StoredToken>;
      if (typeof parsed.token !== 'string' || typeof parsed.savedAt !== 'number') {
        clearSunoToken();
        return null;
      }
      entry = { token: parsed.token, savedAt: parsed.savedAt };
    } catch {
      clearSunoToken();
      return null;
    }
  } else {
    // Legacy bare-string entry — migrate so the next load sees the new
    // schema and can apply TTL.
    entry = { token: raw, savedAt: Date.now() };
    try { localStorage.setItem(TOKEN_KEY, JSON.stringify(entry)); } catch {}
  }

  if (Date.now() - entry.savedAt > TOKEN_TTL_MS) {
    clearSunoToken();
    return null;
  }
  return entry.token;
}

export function saveSunoToken(token: string): void {
  const entry: StoredToken = { token, savedAt: Date.now() };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(entry));
}

export function clearSunoToken(): void {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

/** True when both the proxy URL is configured AND the user has a cookie saved. */
export function hasSunoToken(): boolean {
  return !!loadSunoToken();
}

/**
 * Extracts a Suno song ID from a share URL like
 * `https://suno.com/song/<uuid>` or just a bare UUID. Returns null if the
 * input doesn't look like either.
 */
export function parseSunoUrl(input: string): string | null {
  const trimmed = input.trim();
  const uuidRe = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
  const match = trimmed.match(uuidRe);
  return match ? match[0] : null;
}

function authHeaders(): Record<string, string> {
  const token = loadSunoToken();
  if (!token) {
    throw new Error(
      'No Suno cookie saved — click the ⚙ gear in the "Generate with ' +
      'Suno AI" section and paste your cookie first.',
    );
  }
  const headers: Record<string, string> = { 'X-Suno-Cookie': token };
  // Shared-secret gate on the proxy. Ships in the bundle (so not a
  // real secret), but blocks drive-by curl abuse that skips CORS.
  const apiKey = (import.meta.env.VITE_SUNO_API_KEY as string | undefined) || '';
  if (apiKey) headers['X-API-Key'] = apiKey;
  return headers;
}

/**
 * Try the request against the primary proxy URL; on infrastructure failure
 * (timeout, 5xx) retry the secondary. 4xx responses (auth/rate-limit) are
 * returned as-is without fallback — those are the user's fault and
 * retrying won't help.
 *
 * Primary gets a short timeout (PRIMARY_TIMEOUT_MS) because Vercel free
 * will kill the function at 10s anyway; we notice the abort and move on
 * rather than waiting around.
 *
 * The last URL (typically a Render free-tier fallback) gets
 * FALLBACK_MAX_ATTEMPTS attempts spaced FALLBACK_RETRY_DELAY_MS apart so a
 * cold-start `ERR_CONNECTION_CLOSED` / 502 / 503 doesn't fail the whole
 * generation — one of the later attempts lands once the container is up.
 * `onStatus` surfaces retry progress to the UI.
 */
async function fetchWithFallback(
  buildUrl: (base: string) => string,
  init: RequestInit,
  opts: { onStatus?: (msg: string) => void } = {},
): Promise<Response> {
  const urls = getApiUrls();
  if (urls.length === 0) throw new Error('Suno is not configured on this site.');

  let lastError: unknown = null;
  for (let i = 0; i < urls.length; i++) {
    const base = urls[i]!;
    const isPrimary = i === 0 && urls.length > 1;
    const isLast = i === urls.length - 1;
    const maxAttempts = isLast ? FALLBACK_MAX_ATTEMPTS : 1;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const controller = new AbortController();
      const timer = isPrimary
        ? setTimeout(() => controller.abort(), PRIMARY_TIMEOUT_MS)
        : null;
      const t0 = performance.now();
      try {
        const res = await fetch(buildUrl(base), { ...init, signal: controller.signal });
        if (timer) clearTimeout(timer);
        const elapsed = (performance.now() - t0).toFixed(0);
        if (!res.ok && RETRYABLE_STATUSES.has(res.status)) {
          // 5xx on a non-last URL → move to the next URL immediately.
          if (!isLast) {
            console.warn(
              `[suno] primary HTTP ${res.status} from ${base} after ${elapsed}ms — falling back`,
            );
            lastError = new Error(`HTTP ${res.status} from ${base}`);
            break;
          }
          // 5xx on the last URL → likely cold-start; back off and retry.
          if (attempt < maxAttempts) {
            console.warn(
              `[suno] fallback HTTP ${res.status} from ${base} after ${elapsed}ms (attempt ${attempt}/${maxAttempts}) — retrying in ${FALLBACK_RETRY_DELAY_MS}ms`,
            );
            lastError = new Error(`HTTP ${res.status} from ${base}`);
            opts.onStatus?.(
              `Waking up generator (cold start, retry ${attempt + 1}/${maxAttempts})…`,
            );
            await sleep(FALLBACK_RETRY_DELAY_MS);
            continue;
          }
        }
        return res;
      } catch (err) {
        if (timer) clearTimeout(timer);
        lastError = err;
        const elapsed = (performance.now() - t0).toFixed(0);
        const label = err instanceof DOMException && err.name === 'AbortError'
          ? `aborted after ${elapsed}ms (timeout)`
          : `fetch failed after ${elapsed}ms: ${err instanceof Error ? err.message : String(err)}`;
        // Non-last URL → abandon this URL, try the next one.
        if (!isLast) {
          console.warn(`[suno] primary ${label} from ${base} — falling back`);
          break;
        }
        // Last URL → retry if we have attempts left (cold-start case).
        if (attempt < maxAttempts) {
          console.warn(
            `[suno] fallback ${label} from ${base} (attempt ${attempt}/${maxAttempts}) — retrying in ${FALLBACK_RETRY_DELAY_MS}ms`,
          );
          opts.onStatus?.(
            `Waking up generator (cold start, retry ${attempt + 1}/${maxAttempts})…`,
          );
          await sleep(FALLBACK_RETRY_DELAY_MS);
          continue;
        }
        console.warn(`[suno] fallback ${label} from ${base} — giving up after ${maxAttempts} attempts`);
        throw err;
      }
    }
  }
  throw lastError ?? new Error('All Suno proxy URLs exhausted');
}

/** POST /api/generate. Returns an array of song metadata (status may be "queued"). */
export async function generateFromPrompt(
  prompt: string,
  makeInstrumental: boolean = false,
  model: SunoModelId = loadSunoModel(),
  opts: { onStatus?: (msg: string) => void } = {},
): Promise<SunoSongMeta[]> {
  const res = await fetchWithFallback(
    (base) => `${trimTrailingSlash(base)}/api/generate`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({
        prompt,
        make_instrumental: makeInstrumental,
        model,
        wait_audio: false,
      }),
    },
    opts,
  );
  if (!res.ok) await throwFriendlyError(res, 'generate');
  return normalizeSongList(await res.json());
}

/** GET /api/get?ids=<id> — returns a single song's current status. */
export async function fetchSongById(id: string): Promise<SunoSongMeta> {
  const res = await fetchWithFallback(
    (base) => `${trimTrailingSlash(base)}/api/get?ids=${encodeURIComponent(id)}`,
    { headers: { ...authHeaders() } },
  );
  if (!res.ok) await throwFriendlyError(res, 'get');
  const list = normalizeSongList(await res.json());
  if (list.length === 0) throw new Error(`Suno returned no songs for id ${id}`);
  return list[0]!;
}

/**
 * Poll `fetchSongById` until `audio_url` is populated or `timeoutMs` elapses.
 * Suno generation typically takes 30–90 seconds.
 */
export async function waitForSong(
  id: string,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onTick?: (song: SunoSongMeta) => void;
  } = {},
): Promise<SunoSongMeta> {
  const intervalMs = opts.intervalMs ?? 4000;
  const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
  const started = Date.now();
  while (true) {
    const song = await fetchSongById(id);
    opts.onTick?.(song);
    if (song.audio_url) return song;
    if (Date.now() - started > timeoutMs) {
      throw new Error(`Suno generation timed out (${(timeoutMs / 1000).toFixed(0)}s)`);
    }
    await sleep(intervalMs);
  }
}

// --- helpers ---

function trimTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Translate proxy errors into user-friendly messages.
 *   - 401 → user's cookie is missing/expired
 *   - 402 / "credits" → user's Suno account out of credits
 *   - "ZERO_BALANCE" → site operator's 2Captcha budget exhausted
 *   - 429 → rate-limited
 */
async function throwFriendlyError(res: Response, op: string): Promise<never> {
  const rawBody = await res.text().catch(() => '');
  const bodyLower = rawBody.toLowerCase();
  if (res.status === 401) {
    throw new Error(
      'Your Suno cookie is missing or expired. Click the ⚙ gear and paste a fresh one from suno.com.',
    );
  }
  if (res.status === 402 || bodyLower.includes('credits')) {
    throw new Error(
      'Your Suno account is out of credits for this period. Try again after your quota resets.',
    );
  }
  if (bodyLower.includes('zero_balance') || bodyLower.includes('2captcha')) {
    throw new Error(
      'Song generation is temporarily unavailable (site-wide captcha budget exhausted). ' +
      'Try again later, or paste a Suno share link / direct MP3 URL.',
    );
  }
  if (res.status === 429) {
    throw new Error('Rate-limited by Suno — wait a minute and try again.');
  }
  // 5xx bodies can contain proxy-internal detail (stack traces, headers,
  // upstream error payloads) that we shouldn't surface to users. Only echo
  // the body for 4xx, which is assumed to be a user-actionable message.
  const bodySuffix = rawBody && res.status >= 400 && res.status < 500
    ? ` — ${rawBody.slice(0, 200)}`
    : '';
  throw new Error(
    `Suno /${op} failed: HTTP ${res.status} ${res.statusText}${bodySuffix}`,
  );
}

function normalizeSongList(data: unknown): SunoSongMeta[] {
  if (Array.isArray(data)) return data as SunoSongMeta[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as SunoSongMeta[];
    if (Array.isArray(obj.clips)) return obj.clips as SunoSongMeta[];
    if (typeof obj.id === 'string') return [obj as unknown as SunoSongMeta];
  }
  throw new Error(`Unexpected Suno response shape: ${JSON.stringify(data).slice(0, 200)}`);
}
